import React, { useState, useCallback, useEffect, useRef } from "react";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { UseFormRegisterReturn, FieldError } from "react-hook-form";
import SearchableMultiSelectMenuAddOns from "@/app/components/ui/SelectMenus/MultiSelectMenuAddOns";

interface IDiagnosisSelectorProps {
  title: string;
  data: ISelectMenuItemData[];
  placeholder?: string;
  onSelectionChange?: (selectedItems: ISelectMenuItemData[]) => void;
  onBlur?: () => void;
  onAddNewDiagnosis?: (diagnosisName: string) => void;
  register?: UseFormRegisterReturn<string>;
  fieldError?: any;
  errorMessages?: Array<{ type: string; message: string }>;
  disabled?: boolean;
  required?: boolean;
  defaultSelected?: ISelectMenuItemData[];
}

const DiagnosisSelector: React.FC<IDiagnosisSelectorProps> = ({
  title,
  data,
  placeholder = "Search Diagnosis",
  onSelectionChange,
  onBlur,
  onAddNewDiagnosis,
  register,
  fieldError,
  errorMessages = [],
  disabled = false,
  required = false,
  defaultSelected = [],
}) => {
  const [selectedDiagnoses, setSelectedDiagnoses] = useState<ISelectMenuItemData[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const hasInteracted = useRef(false);
  const isInitialized = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedDiagnoses(defaultSelected || []);
    
    if (!isInitialized.current && defaultSelected && defaultSelected.length > 0) {
      isInitialized.current = true;
    }
  }, [defaultSelected]);

  const handleSelectDiagnoses = useCallback(
    (items: ISelectMenuItemData[]) => {
      hasInteracted.current = true;
      // Filter out null/undefined values to prevent errors
      const validItems = items?.filter(item => item !== null && item !== undefined) || [];
      setSelectedDiagnoses(validItems);
      if (onSelectionChange) {
        onSelectionChange(validItems);
      }
    },
    [onSelectionChange]
  );

  const handleRemoveDiagnosis = useCallback(
    (diagnosisId: string) => {
      hasInteracted.current = true;
      const updatedDiagnoses = selectedDiagnoses.filter((item) => item.id !== diagnosisId);
      setSelectedDiagnoses(updatedDiagnoses);
      if (onSelectionChange) {
        onSelectionChange(updatedDiagnoses);
      }
      if (onBlur) {
        setTimeout(() => {
          onBlur();
        }, 50);
      }
    },
    [selectedDiagnoses, onSelectionChange, onBlur]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      const currentTarget = e.currentTarget;
      
      setTimeout(() => {
        const isOutsideComponent = !currentTarget.contains(document.activeElement);
        
        if (isOutsideComponent && !isDropdownOpen && hasInteracted.current && onBlur) {
          onBlur();
          hasInteracted.current = false; 
        }
      }, 150); 
    },
    [isDropdownOpen, onBlur]
  );

  const handleDropdownStateChange = useCallback((isOpen: boolean) => {
    const wasOpen = isDropdownOpen;
    setIsDropdownOpen(isOpen);
    
    if (wasOpen && !isOpen && hasInteracted.current && onBlur) {
      setTimeout(() => {
        onBlur();
        hasInteracted.current = false; 
      }, 100);
    }
  }, [isDropdownOpen, onBlur]);

  const handleAddNewDiagnosis = useCallback(
    (diagnosisName: string) => {
      if (!diagnosisName || diagnosisName.trim() === "") {
        return;
      }

      const trimmedName = diagnosisName.trim();

      const existingDiagnosis = data.find(
        (item) => item.title.toLowerCase() === trimmedName.toLowerCase()
      );

      if (existingDiagnosis) {
        const isAlreadySelected = selectedDiagnoses.some(
          (item) => item.id === existingDiagnosis.id
        );
        if (!isAlreadySelected) {
          const updatedSelection = [...selectedDiagnoses, existingDiagnosis];
          handleSelectDiagnoses(updatedSelection);
        }
        return;
      }

      const isAlreadySelected = selectedDiagnoses.some(
        (item) => item.id === trimmedName || item.title.toLowerCase() === trimmedName.toLowerCase()
      );

      if (isAlreadySelected) {
        return; // Don't add duplicates
      }

      const newDiagnosisItem: ISelectMenuItemData = {
        id: trimmedName,
        title: trimmedName,
        state: {},
      };

      hasInteracted.current = true;
      const updatedSelection = [...selectedDiagnoses, newDiagnosisItem];
      setSelectedDiagnoses(updatedSelection);

      if (onSelectionChange) {
        onSelectionChange(updatedSelection);
      }

      if (onBlur) {
        setTimeout(() => {
          onBlur();
        }, 50);
      }
    },
    [data, selectedDiagnoses, handleSelectDiagnoses, onSelectionChange, onBlur]
  );

  const dummyRegister: UseFormRegisterReturn<string> = register || {
    name: `diagnosis-${title.toLowerCase().replace(/\s+/g, "-")}`,
    onChange: async () => Promise.resolve(),
    onBlur: async () => Promise.resolve(),
    ref: () => {},
  };

  const normalizedFieldError = fieldError as FieldError | undefined;

  return (
    <div 
      ref={containerRef}
      className="w-full"
      onBlur={handleBlur}
      tabIndex={-1}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <h3 className="shrink-0 text-sm font-medium text-black whitespace-nowrap">
          {title}
          {required && <span className="text-rose-600 ml-1">*</span>}
        </h3>
        <div className="w-full sm:max-w-md">
          <div className="relative">
            
            <SearchableMultiSelectMenuAddOns
              selectItems={data}
              onSelectItem={handleSelectDiagnoses}
              placeholder={placeholder}
              withDropDownSearch={true}
              className="text-sm"
              containerClassName="w-full"
              listBoxClassName="max-h-60"
              showTypedErrors={false}
              register={dummyRegister}
              fieldError={normalizedFieldError}
              errorMessages={errorMessages}
              defaultSelected={selectedDiagnoses}
              disabled={disabled}
              watchCascadeSelectMenuState={handleDropdownStateChange}
              addAction={{
                title: "+ Add",
                callBack: handleAddNewDiagnosis,
              }}
            />
          </div>
        </div>
      </div>

      {/* Selected Diagnoses Tags */}
      {selectedDiagnoses.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {selectedDiagnoses.map((diagnosis) => (
            <div
              key={diagnosis.id}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F7F6F6] rounded-2xl text-sm text-gray-700"
            >
              <span>{diagnosis.title}</span>
              <button
                type="button"
                onClick={() => handleRemoveDiagnosis(diagnosis.id)}
                className="text-[#FF0000] hover:text-red-700 focus:outline-none"
                aria-label={`Remove ${diagnosis.title}`}
                disabled={disabled}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DiagnosisSelector;
